import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Upload,
} from "antd";
import { useState } from "react";
import * as XLSX from "xlsx";
import { useImportStationeryProduct } from "../../../query/HrmQuery";
import { notify } from "../../../utils/toast";

interface Props {
  isOpen: boolean;
  onCancel: () => void;
}

const EXPECTED_HEADERS = [
  "STT",
  "Quy cách",
  "ĐVT",
  "Đơn giá       ( chưa VAT)",
];

export const ModalImportStationeryProduct = ({ isOpen, onCancel }: Props) => {
  const [importForm] = Form.useForm();
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [previewColumns, setPreviewColumns] = useState<any[]>([]);
  const importMutation = useImportStationeryProduct();

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([EXPECTED_HEADERS]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Template_Import_VPP.xlsx");
  };

  const handleFileChange = (info: any) => {
    const fileList = Array.isArray(info) ? info : info?.fileList;
    const file = fileList?.[0]?.originFileObj || fileList?.[0];

    if (!file) {
      setPreviewData([]);
      setPreviewColumns([]);
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;

        const workbook = XLSX.read(buffer, {
          type: "array",
        });

        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          blankrows: false,
        }) as any[][];

        const headerIndex = rows.findIndex(
          (row) =>
            row.some(
              (cell) =>
                String(cell || "")
                  .trim()
                  .toLowerCase() === "quy cách",
            ) &&
            row.some(
              (cell) =>
                String(cell || "")
                  .trim()
                  .toLowerCase() === "đvt",
            ),
        );

        if (headerIndex === -1) {
          notify.error("Không tìm thấy tiêu đề trong file Excel");
          return;
        }

        const headers = rows[headerIndex];
        const sttIndex = headers.findIndex((h) => String(h).trim() === "STT");
        const selectedColumns = headers
          .map((header, index) => ({
            title: String(header || "").trim(),
            index,
          }))
          .filter(
            (item) => item.title && !item.title.toLowerCase().includes("hình"),
          );

        const dataRows = rows.slice(headerIndex + 1).filter((row) => {
          const stt = row[sttIndex];

          return stt !== undefined && stt !== null && !isNaN(Number(stt));
        });

        const preview = dataRows.map((row, index) => {
          const item: any = {
            key: index,
          };

          selectedColumns.forEach((column) => {
            item[column.title] = row[column.index];
          });

          return item;
        });

        const columns = selectedColumns.map((column) => {
          const lowerTitle = column.title.toLowerCase();

          return {
            title: column.title,
            dataIndex: column.title,
            key: column.title,
            width: lowerTitle.includes("quy cách")
              ? 450
              : lowerTitle.includes("đvt")
                ? 100
                : lowerTitle.includes("stt")
                  ? 80
                  : 150,
            render: (value: any) => {
              let displayValue = value;

              if (
                lowerTitle.includes("đơn giá") &&
                value &&
                !isNaN(Number(value))
              ) {
                displayValue =
                  Number(value * 1000).toLocaleString("vi-VN") + " đ";
              }

              return (
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={String(displayValue || "")}
                >
                  {displayValue}
                </div>
              );
            },
          };
        });

        setPreviewColumns(columns);
        setPreviewData(preview);
      } catch (error) {
        console.error(error);

        notify.error("Có lỗi khi đọc file Excel");

        setPreviewColumns([]);
        setPreviewData([]);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImport = async (values: { file: any[] }) => {
    const file = values.file?.[0]?.originFileObj || values.file?.[0];
    if (!file) {
      notify.error("Không tìm thấy file!");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    try {
      await importMutation.mutateAsync(formData);
      notify.success("Import thành công!");
      handleClose();
    } catch (error) {
      notify.error("Import thất bại!");
    }
  };

  const handleClose = () => {
    onCancel();
    importForm.resetFields();
    setPreviewData([]);
    setPreviewColumns([]);
  };

  return (
    <Modal
      title="Import danh mục từ Excel"
      open={isOpen}
      onCancel={handleClose}
      width={1000}
      footer={null}
      destroyOnClose
    >
      <Form form={importForm} onFinish={handleImport} layout="vertical">
        <Row gutter={16} align="middle" className="mb-4">
          <Col span={12}>
            <Space align="start">
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
              >
                Tải mẫu Excel
              </Button>
              <Form.Item
                name="file"
                valuePropName="fileList"
                getValueFromEvent={(e: any) =>
                  Array.isArray(e) ? e : e?.fileList
                }
                rules={[
                  { required: true, message: "Vui lòng chọn file!" },
                  { type: "array", min: 1, message: "Vui lòng chọn file!" },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Upload
                  accept=".xlsx, .xls"
                  beforeUpload={() => false}
                  maxCount={1}
                  onChange={handleFileChange}
                  showUploadList={true}
                >
                  <Button htmlType="button" icon={<UploadOutlined />}>
                    Chọn file Excel
                  </Button>
                </Upload>
              </Form.Item>
            </Space>
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={handleClose}>Hủy</Button>
              <Popconfirm
                title="Tải lên dữ liệu?"
                description="Bạn có chắc chắn muốn tải lên dữ liệu Excel này?"
                onConfirm={() => importForm.submit()}
                disabled={previewData.length === 0}
              >
                <Button
                  type="primary"
                  loading={importMutation.isPending}
                  disabled={previewData.length === 0}
                >
                  Tải lên dữ liệu
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        </Row>

        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: "8px",
            padding: "16px",
            backgroundColor: "#fafafa",
          }}
        >
          {previewData.length > 0 ? (
            <Table
              rowKey="key"
              dataSource={previewData}
              columns={previewColumns}
              bordered
              size="small"
              pagination={{
                pageSize: 20,
                showSizeChanger: false,
              }}
              scroll={{
                x: 1200,
                y: 500,
              }}
            />
          ) : (
            <div
              style={{ textAlign: "center", padding: "40px 0", color: "#999" }}
            >
              <UploadOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <p>Vui lòng để xem trước dữ liệu</p>
            </div>
          )}
        </div>
      </Form>
    </Modal>
  );
};
